// The territory border as traced GEOMETRY: the one border of the map
// (R495, R532).
//
// The kernel field of R431 drew territory as a smooth scalar field, and it
// had a known cost: a small claim fell under the kernel and had to come
// back as a hex, and a generic border angle carried a wobble the field
// could not beat at every radius. The traced polygon has neither problem,
// so the owner made it the default on 2026-09-05 (R532), and the field
// code left the tree on 2026-11-03.
//
// This module builds the polygon on the CPU. It touches no WebGL and no
// DOM, so a test runs all of it.
//
// The four steps of `docs/design/ui-map-interface.md` §4.2 are the PixiJS
// client's, and this module reuses them as they stand:
//
//   1. trace the cell edges of one faction into closed loops,
//   2. relax the loops six times, so the hex staircase goes flat,
//   3. put back the area the relaxation took, so a small claim survives,
//   4. cut the corners three times, so the line reads as a curve.
//
// `outlineLoops()` of `render/outlines.ts` is all four. It imports no
// engine, so the import graph is safe (`nopixi.test.ts`).
//
// The loops become two meshes:
//
//   FILL — `earcut` triangulates each outer loop with its own holes, so a
//   pocket inside sovereign water is cut out of the fill and not painted
//   over. `loopsWithHoles()` does the pairing, because a hole is only a
//   hole of the boundary it sits inside (R96).
//
//   STROKE — a closed polyline becomes a triangle strip. Every point
//   carries a miter normal, and the two vertices of the point sit on the
//   same x, y with opposite normals. The strip is expressed as indexed
//   triangles, so one draw call covers the whole faction. Every point also
//   carries its ARC LENGTH from the start of its loop and the length of
//   the whole loop, because the faction dash of the line pass runs along
//   the loop (R1568, R1577). The strip repeats the first point at the end,
//   at the whole length, so the closing edge counts on to the end of the
//   loop and does not run back to 0.
//
// The normal is UNIT scale: the vertex shader multiplies it by the half
// width of the pass. A width knob and a zoom-dependent width therefore
// cost no rebuild of the mesh, and the three glow passes of the PixiJS
// border draw from one buffer.
//
// Only the OUTER loops are stroked. A glow drawn round an enclosed pocket
// lights the pocket from every side at once, which made an unclaimed cell
// inside sovereign water the brightest thing on the map (R90).
//
// The trace reads a plane where a ridge that one claim fully surrounds
// belongs to that claim (`rockFilledOwners`, R498), so the outline runs
// round a ridge on a coast and draws no hole round one inside. The wash is
// still cut by the rock mask in the shader; the glow is not, so it draws
// over the ridge (R496).

import earcut from 'earcut';
import { loopsWithHoles, outerLoops, type Loop } from '../render/contours';
import { outlineLoops } from '../render/outlines';
import { neighborAt } from '../world';
import { BLOCK_RIDGE } from '../gen/balance';

/**
 * The owner plane with every ENCLOSED ridge given to the claim round it
 * (R498).
 *
 * The server never lets a claim take a ridge cell (R103), so a trace of
 * the raw owner plane cuts a hole at every ridge. The owner ruled on
 * 2026-09-04 that the outline still runs round a ridge on a coast or
 * between two claims, and that a ridge ONE claim fully surrounds is inside
 * that claim and gets no hole. This function walks every connected group
 * of rock cells once and reads the owners of the water cells that touch
 * it. When every such cell belongs to one faction, and the group touches
 * no unowned water and no grid edge, the group takes that faction. Any
 * other group stays unowned, and the trace wraps it.
 *
 * The walk visits cells in index order, so the result is the same for the
 * same input on every machine (CLAUDE.md, determinism). It runs once per
 * frame with a retrace, over every cell, and it costs one pass of the
 * plane.
 *
 * `blocked` is the terrain kind of every cell; `BLOCK_RIDGE` marks a ridge.
 * It returns `owners` itself when the plane holds no rock.
 */
export function rockFilledOwners(
  owners: Uint8Array, blocked: Uint8Array, cols: number, rows: number,
): Uint8Array {
  const n = cols * rows;
  let anyRock = false;
  for (let i = 0; i < n; i++) {
    if (blocked[i] === BLOCK_RIDGE) { anyRock = true; break; }
  }
  if (!anyRock) return owners;
  const out = new Uint8Array(owners);
  const seen = new Uint8Array(n);
  const group: number[] = [];
  for (let start = 0; start < n; start++) {
    if (blocked[start] !== BLOCK_RIDGE || seen[start]) continue;
    // One connected group of rock, and the one faction round it, or 0
    // when the group touches unowned water, a second faction or the edge.
    group.length = 0;
    group.push(start);
    seen[start] = 1;
    let faction = -1;
    for (let head = 0; head < group.length; head++) {
      const i = group[head];
      for (let k = 0; k < 6; k++) {
        const j = neighborAt(i, k, cols, rows);
        if (j < 0) { faction = 0; continue; }
        if (blocked[j] === BLOCK_RIDGE) {
          if (!seen[j]) { seen[j] = 1; group.push(j); }
          continue;
        }
        const own = owners[j];
        if (own === 0) faction = 0;
        else if (faction === -1) faction = own;
        else if (faction !== own) faction = 0;
      }
    }
    if (faction > 0) for (const i of group) out[i] = faction;
  }
  return out;
}

/** One indexed triangle mesh: an interleaved vertex array and its indices. */
export interface MeshData {
  verts: Float32Array;
  idx: Uint32Array;
}

/** The two meshes of one faction, and the size of the trace behind them. */
export interface FactionGeometry {
  /** Vertices of `[x, y]`, two floats each. */
  fill: MeshData;
  /** Vertices of `[x, y, nx, ny, side, arc, loop]`, seven floats each. */
  stroke: MeshData;
  /** The number of smoothed loop points, for the stats line. */
  points: number;
}

/** The relax count of §4.2. */
const RELAX = 6;

/** The corner-cut count of §4.2. */
const CHAIKIN = 3;

/** Floats per fill vertex: x, y. */
export const FILL_STRIDE = 2;

/**
 * Floats per stroke vertex: x, y, nx, ny, side, arc, loop.
 *
 * `arc` is the length along the loop from its first point and `loop` is the
 * length of the whole loop, both in world px (R1577).
 */
export const STROKE_STRIDE = 7;

/** An edge shorter than this is a duplicate point and carries no direction. */
const EPS = 1e-6;

/** The miter cap. A corner sharper than this gets a shorter normal. */
const MITER_MIN = 0.5;

const EMPTY: MeshData = { verts: new Float32Array(0), idx: new Uint32Array(0) };

/**
 * The fill mesh and the stroke mesh of one faction.
 *
 * `owners` is the owner class of every cell, `id` is the faction to trace,
 * and `cols` by `rows` is the live grid. The caller passes the plane of
 * `rockFilledOwners` so an enclosed ridge gets no hole (R498). A
 * faction that owns nothing gives two empty meshes and zero points.
 */
export function buildFactionGeometry(
  owners: Uint8Array, id: number, cols: number, rows: number,
): FactionGeometry {
  const loops = outlineLoops(owners, id, RELAX, CHAIKIN, cols, rows);
  if (loops.length === 0) return { fill: EMPTY, stroke: EMPTY, points: 0 };

  let points = 0;
  for (const loop of loops) points += loop.length;

  return { fill: buildFill(loops), stroke: buildStroke(loops), points };
}

/** Every outer loop with its holes, triangulated into one mesh. */
function buildFill(loops: Loop[]): MeshData {
  const verts: number[] = [];
  const idx: number[] = [];

  for (const { outer, holes } of loopsWithHoles(loops)) {
    // earcut wants one flat coordinate array, and the index at which each
    // hole starts. The holes follow the outer boundary in that same array.
    const flat: number[] = [];
    for (const [x, y] of outer) flat.push(x, y);
    const holeStarts: number[] = [];
    for (const hole of holes) {
      holeStarts.push(flat.length / FILL_STRIDE);
      for (const [x, y] of hole) flat.push(x, y);
    }

    append(verts, idx, flat, earcut(flat, holeStarts, FILL_STRIDE), FILL_STRIDE);
  }

  return pack(verts, idx);
}

/** Every outer loop as a strip. The holes are not stroked (R90). */
function buildStroke(loops: Loop[]): MeshData {
  const verts: number[] = [];
  const idx: number[] = [];

  for (const loop of outerLoops(loops)) {
    const strip = stripLoop(loop);
    append(verts, idx, strip.verts, strip.idx, STROKE_STRIDE);
  }

  return pack(verts, idx);
}

/**
 * Adds one piece to the builder arrays. Its indices are rebased onto the
 * vertices already there, so every piece of a faction is one mesh.
 */
function append(
  verts: number[], idx: number[],
  v: ArrayLike<number>, i: ArrayLike<number>, stride: number,
): void {
  const base = verts.length / stride;
  for (let k = 0; k < v.length; k++) verts.push(v[k]);
  for (let k = 0; k < i.length; k++) idx.push(base + i[k]);
}

/**
 * The arc length of every point of a closed loop, from its first point.
 *
 * The answer holds `n + 1` lengths for `n` points: the last one is the
 * length of the whole loop, the closing edge included, which is where the
 * strip's repeated first point stands (R1577). The loop is the one
 * `stripLoop` keeps, so the caller removes the repeated points first.
 */
export function loopArcs(pts: Loop): Float64Array {
  const n = pts.length;
  const out = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[(i + 1) % n];
    out[i + 1] = out[i] + Math.hypot(bx - ax, by - ay);
  }
  return out;
}

/**
 * A closed polyline as a triangle strip, expressed as indexed triangles.
 *
 * Each kept point gives two vertices on the same x, y: one at `+n` and one
 * at `-n`, marked by the `side` float. The normal is the miter normal of
 * the corner at unit scale, so the shader sets the width. Both vertices
 * carry the arc length of the point and the length of the loop, so the
 * dash of the line pass runs along the loop (R1568, R1577).
 *
 * The strip holds `n + 1` vertex pairs for `n` points: the last pair is
 * the first point again, at the whole length of the loop. The closing edge
 * then counts from the last point's arc on to the whole length, and the
 * dash pattern ends where it began.
 *
 * A loop of fewer than three kept points has no interior and gives an
 * empty mesh.
 */
export function stripLoop(loop: Loop): MeshData {
  const pts = dedupe(loop);
  const n = pts.length;
  if (n < 3) return EMPTY;

  const verts = new Float32Array((n + 1) * 2 * STROKE_STRIDE);
  const idx = new Uint32Array(n * 6);
  const arcs = loopArcs(pts);
  const total = arcs[n];

  for (let i = 0; i < n; i++) {
    const [px, py] = pts[(i - 1 + n) % n];
    const [cx, cy] = pts[i];
    const [qx, qy] = pts[(i + 1) % n];

    // The two unit edge directions that meet at this point.
    const [e0x, e0y] = unit(cx - px, cy - py);
    const [e1x, e1y] = unit(qx - cx, qy - cy);

    // The tangent bisects them. A point where the line doubles back has no
    // bisector, so the perpendicular of the incoming edge stands in.
    let [tx, ty] = unit(e0x + e1x, e0y + e1y);
    if (tx === 0 && ty === 0) { tx = -e0y; ty = e0x; }

    // The normal is the perpendicular of the tangent, and the miter scale
    // is 1 / cos(half the turn). The cap keeps a spike finite.
    const nx = -ty, ny = tx;
    const m = 1 / Math.max(nx * -e0y + ny * e0x, MITER_MIN);
    const mx = nx * m, my = ny * m;

    writePair(verts, i, cx, cy, mx, my, arcs[i], total);
    // The first point again, at the whole length: the pair the last edge
    // closes on (R1577).
    if (i === 0) writePair(verts, n, cx, cy, mx, my, total, total);

    // The quad of the edge that leaves this point. The last edge ends on
    // the repeated first pair, which is what closes the loop.
    const a = i * 2, b = a + 1;
    const c = a + 2, d = a + 3;
    const e = i * 6;
    idx[e] = a; idx[e + 1] = b; idx[e + 2] = c;
    idx[e + 3] = b; idx[e + 4] = d; idx[e + 5] = c;
  }

  return { verts, idx };
}

/** Writes the two vertices of one strip point, `+n` and then `-n`. */
function writePair(
  verts: Float32Array, pair: number, x: number, y: number,
  mx: number, my: number, arc: number, total: number,
): void {
  const v = pair * 2 * STROKE_STRIDE;
  verts[v] = x; verts[v + 1] = y;
  verts[v + 2] = mx; verts[v + 3] = my; verts[v + 4] = 1;
  verts[v + 5] = arc; verts[v + 6] = total;
  const w = v + STROKE_STRIDE;
  verts[w] = x; verts[w + 1] = y;
  verts[w + 2] = -mx; verts[w + 3] = -my; verts[w + 4] = -1;
  verts[w + 5] = arc; verts[w + 6] = total;
}

/** The loop without the points that repeat, the wrap included. */
function dedupe(loop: Loop): Loop {
  const out: Loop = [];
  for (const p of loop) {
    const last = out[out.length - 1];
    if (last !== undefined && Math.hypot(p[0] - last[0], p[1] - last[1]) < EPS) continue;
    out.push(p);
  }
  while (out.length > 1) {
    const first = out[0], last = out[out.length - 1];
    if (Math.hypot(last[0] - first[0], last[1] - first[1]) >= EPS) break;
    out.pop();
  }
  return out;
}

/** The direction of a vector, or zero when it has no length. */
function unit(x: number, y: number): [number, number] {
  const d = Math.hypot(x, y);
  return d < EPS ? [0, 0] : [x / d, y / d];
}

/** The two builder arrays as typed arrays. */
function pack(verts: number[], idx: number[]): MeshData {
  return { verts: Float32Array.from(verts), idx: Uint32Array.from(idx) };
}
